import "@/App.css";
import samperlabsLogoUrl from "@/assets/samperlabs-logo.svg";
import { useRef, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import {
  ArrowRight,
  Activity,
  BarChart3,
  Database,
  FileText,
  Shield,
  Layers,
  Truck,
  Scale,
  ClipboardCheck,
  TrendingUp,
  Link2,
  Zap,
  Target,
  Globe,
  ChevronRight,
  Menu,
  X,
  Check,
  Cpu,
  Box,
  GitBranch
} from "lucide-react";

// Logo: vector mark bundled from src/assets (source also in public/)
const SAMPERLABS_LOGO = samperlabsLogoUrl;
const POMET_LOGO = "https://customer-assets.emergentagent.com/job_architects-hub-1/artifacts/j1m0am4d_image.png";

// Animation variants - Enhanced scroll animations
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } 
  }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1, 
    transition: { duration: 0.5 } 
  }
};

const scaleUp = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { 
    opacity: 1, 
    scale: 1, 
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } 
  }
};

const slideInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { 
    opacity: 1, 
    x: 0, 
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } 
  }
};

const slideInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { 
    opacity: 1, 
    x: 0, 
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } 
  }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } }
};

const staggerFast = {
  visible: { transition: { staggerChildren: 0.08 } }
};

// Animated counter hook
const useCounter = (end, duration = 2000, inView) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!inView) return;
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [end, duration, inView]);
  
  return count;
};

/** EN/TR control — shown only inside the mobile menu drawer. */
const LangSwitch = () => {
  const { t, i18n } = useTranslation();
  const code = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const isEn = code === "en";
  const isTr = code === "tr";

  return (
    <div
      className="mt-3 flex w-full items-center justify-center gap-0.5 rounded-lg border border-white/10 p-0.5"
      role="group"
      aria-label={`${t("nav.langEn")} / ${t("nav.langTr")}`}
    >
      <button
        type="button"
        onClick={() => i18n.changeLanguage("en")}
        className={`min-h-[44px] flex-1 rounded-md text-sm font-semibold uppercase tracking-wide transition-colors ${
          isEn ? "bg-white/15 text-white" : "text-white/50 hover:text-white"
        }`}
        aria-pressed={isEn}
        title={t("nav.langEn")}
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => i18n.changeLanguage("tr")}
        className={`min-h-[44px] flex-1 rounded-md text-sm font-semibold uppercase tracking-wide transition-colors ${
          isTr ? "bg-white/15 text-white" : "text-white/50 hover:text-white"
        }`}
        aria-pressed={isTr}
        title={t("nav.langTr")}
      >
        TR
      </button>
    </div>
  );
};

/** Desktop language switcher — Globe icon with animated dropdown */
const DesktopLangSwitch = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const code = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const switchTo = (lng) => {
    i18n.changeLanguage(lng);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-white/60 transition-colors hover:text-white hover:bg-white/[0.06]"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Globe size={16} strokeWidth={1.8} />
        <span className="font-medium uppercase text-xs tracking-wide">{code}</span>
      </button>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -6, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="absolute right-0 top-full mt-2 w-32 origin-top-right overflow-hidden rounded-xl border border-white/10 bg-[#141416]/95 shadow-[0_8px_32px_rgba(0,0,0,0.5)] backdrop-blur-xl"
        >
          <button
            type="button"
            onClick={() => switchTo("en")}
            className={`flex w-full items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors ${
              code === "en"
                ? "bg-white/[0.08] text-white"
                : "text-white/50 hover:bg-white/[0.04] hover:text-white"
            }`}
          >
            <span className="text-xs font-semibold tracking-wide">EN</span>
            <span className="text-white/40 text-xs">English</span>
            {code === "en" && <Check size={13} className="ml-auto text-blue-400" />}
          </button>
          <button
            type="button"
            onClick={() => switchTo("tr")}
            className={`flex w-full items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors ${
              code === "tr"
                ? "bg-white/[0.08] text-white"
                : "text-white/50 hover:bg-white/[0.04] hover:text-white"
            }`}
          >
            <span className="text-xs font-semibold tracking-wide">TR</span>
            <span className="text-white/40 text-xs">Türkçe</span>
            {code === "tr" && <Check size={13} className="ml-auto text-blue-400" />}
          </button>
        </motion.div>
      )}
    </div>
  );
};

// Navigation
const NAV_SCROLL_TOP_REVEAL = 48;
const NAV_SCROLL_DIRECTION_DELTA = 10;

const useHashHref = (fragment) => {
  const { pathname } = useLocation();
  return pathname === "/" ? `#${fragment}` : `/#${fragment}`;
};

const Navigation = () => {
  const { t } = useTranslation();
  const platformHref = useHashHref("platform");
  const visionHref = useHashHref("vision");
  const contactHref = useHashHref("contact");
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navVisible, setNavVisible] = useState(true);
  const lastScrollYRef = useRef(
    typeof window !== "undefined" ? window.scrollY : 0
  );
  const mobileOpenRef = useRef(false);
  mobileOpenRef.current = mobileOpen;

  const closeMobileMenu = useCallback(() => {
    setMobileOpen(false);
    setNavVisible(true);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const last = lastScrollYRef.current;
      setScrolled(y > 20);

      if (mobileOpenRef.current) {
        setNavVisible(true);
        lastScrollYRef.current = y;
        return;
      }

      if (y < NAV_SCROLL_TOP_REVEAL) {
        setNavVisible(true);
      } else if (y > last + NAV_SCROLL_DIRECTION_DELTA) {
        setNavVisible(false);
      } else if (y < last - NAV_SCROLL_DIRECTION_DELTA) {
        setNavVisible(true);
      }

      lastScrollYRef.current = y;
    };

    lastScrollYRef.current = window.scrollY;
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeMobileMenu();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileOpen, closeMobileMenu]);

  useEffect(() => {
    const onResize = () => {
      if (window.matchMedia("(min-width: 768px)").matches) setMobileOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <motion.nav
      initial={{ y: "-100%", opacity: 0 }}
      animate={{
        y: navVisible ? 0 : "-100%",
        opacity: 1
      }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className={`site-nav fixed top-0 left-0 right-0 z-50 w-full will-change-transform transition-[background,backdrop-filter,border-color,padding] duration-300 max-md:border-b-0 max-md:bg-transparent max-md:backdrop-blur-none ${
        navVisible ? "pointer-events-auto" : "pointer-events-none"
      } ${
        scrolled
          ? "nav-scrolled pb-2 pt-[max(0.5rem,env(safe-area-inset-top,0px))]"
          : "pb-3.5 pt-[max(0.8rem,env(safe-area-inset-top,0px))]"
      }`}
      data-testid="navigation"
      inert={!navVisible ? true : undefined}
    >
      <div className="relative mx-auto flex min-h-[4.25rem] max-md:min-h-[3.5rem] max-w-6xl items-center justify-between gap-2 px-4 sm:gap-3 sm:px-6">
        <Link
          to="/"
          className="brand-logo-link z-[1] flex min-w-0 flex-none max-md:ml-[5%] items-center justify-start overflow-hidden pr-2 md:z-auto md:ml-[10%] md:w-auto md:max-w-none md:overflow-hidden md:pr-0"
          data-testid="logo"
          onClick={closeMobileMenu}
        >
          <img
            src={SAMPERLABS_LOGO}
            alt={t("common.altLogo")}
            className="brand-logo-img"
          />
        </Link>

        <div className="hidden items-center gap-6 md:flex">
          <a href={platformHref} className="text-sm text-white/60 transition-colors hover:text-white" data-testid="nav-platform">{t("nav.platform")}</a>
          <a href="https://carnitrack.samperlabs.com" target="_blank" rel="noopener noreferrer" className="text-sm text-white/60 transition-colors hover:text-white" data-testid="nav-carnitrack">{t("nav.carnitrack")}</a>
          <a href="https://agritrack.samperlabs.com" target="_blank" rel="noopener noreferrer" className="text-sm text-white/60 transition-colors hover:text-white" data-testid="nav-agritrack">{t("nav.agritrack")}</a>
          <a href={visionHref} className="text-sm text-white/60 transition-colors hover:text-white" data-testid="nav-vision">{t("nav.vision")}</a>
          <a href={contactHref} className="text-sm text-white/60 transition-colors hover:text-white" data-testid="nav-contact">{t("nav.contact")}</a>
        </div>

        <div className="relative z-[2] flex shrink-0 items-center gap-2 sm:gap-3">
          <div className="hidden md:flex items-center gap-1">
            <DesktopLangSwitch />
          </div>
          <button
            type="button"
            className="btn-primary hidden rounded-lg px-3 py-1.5 text-sm font-medium text-white md:inline-flex"
            data-testid="nav-demo"
          >
            {t("nav.demo")}
          </button>
          <button
            type="button"
            className="relative flex h-11 w-11 shrink-0 touch-manipulation items-center justify-center rounded-lg text-white outline-none focus-visible:ring-2 focus-visible:ring-white/30 md:hidden max-md:bg-white/[0.08] max-md:ring-1 max-md:ring-white/[0.12] max-md:backdrop-blur-sm"
            onClick={() => setMobileOpen((o) => !o)}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav-panel"
            aria-label={mobileOpen ? t("nav.ariaCloseMenu") : t("nav.ariaOpenMenu")}
            data-testid="mobile-toggle"
          >
            {mobileOpen ? <X size={22} strokeWidth={2} aria-hidden /> : <Menu size={22} strokeWidth={2} aria-hidden />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <motion.div
          id="mobile-nav-panel"
          role="dialog"
          aria-modal="true"
          aria-label={t("nav.ariaSiteNav")}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute left-0 right-0 top-full max-h-[min(72vh,calc(100dvh-env(safe-area-inset-top,0px)-5.5rem))] w-full overflow-y-auto overscroll-contain border-b border-white/10 bg-[#0a0a0b]/[0.98] shadow-[0_16px_48px_rgba(0,0,0,0.5)] backdrop-blur-xl md:hidden"
        >
          <div className="flex flex-col px-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))] pt-1 sm:px-6">
            <a
              href={platformHref}
              className="flex min-h-[44px] items-center border-b border-white/[0.06] text-base text-white/90 active:bg-white/5"
              onClick={closeMobileMenu}
            >
              {t("nav.platform")}
            </a>
            <a
              href="https://carnitrack.samperlabs.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-h-[44px] items-center border-b border-white/[0.06] text-base text-white/90 active:bg-white/5"
              onClick={closeMobileMenu}
            >
              {t("nav.carnitrack")}
            </a>
            <a
              href="https://agritrack.samperlabs.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-h-[44px] items-center border-b border-white/[0.06] text-base text-white/90 active:bg-white/5"
              onClick={closeMobileMenu}
            >
              {t("nav.agritrack")}
            </a>
            <a
              href={visionHref}
              className="flex min-h-[44px] items-center border-b border-white/[0.06] text-base text-white/90 active:bg-white/5"
              onClick={closeMobileMenu}
            >
              {t("nav.vision")}
            </a>
            <a
              href={contactHref}
              className="flex min-h-[44px] items-center border-b border-white/[0.06] text-base text-white/90 active:bg-white/5"
              onClick={closeMobileMenu}
            >
              {t("nav.contact")}
            </a>
            <LangSwitch />
            <button
              type="button"
              className="btn-primary mt-3 min-h-[48px] w-full rounded-lg px-4 py-3 text-sm font-medium text-white touch-manipulation"
              onClick={closeMobileMenu}
            >
              {t("nav.demo")}
            </button>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};

// Hero headline: one timer for both breakpoints — stacked on mobile, inline + subtitle on md+.
const HeroHeadlineBlock = () => {
  const { t, i18n } = useTranslation();
  const rawWords = t("hero.words", { returnObjects: true });
  const words = Array.isArray(rawWords) ? rawWords : ["Meat", "Protein", "Livestock"];
  const isTr = i18n.language?.split("-")[0] === "tr";
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % words.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [words.length]);

  if (isTr) {
    const suffix = t("hero.headlineTr");
    return (
      <>
        <span className="mt-1 inline-grid grid-cols-1 grid-rows-1 place-items-start md:hidden" aria-live="polite">
          <span className="invisible col-start-1 row-start-1 font-semibold" aria-hidden>
            {t("hero.invisibleSpacer")}
          </span>
          {words.map((word, index) => (
            <motion.span
              key={`m-${word}`}
              initial={false}
              animate={{
                opacity: currentIndex === index ? 1 : 0,
                y: currentIndex === index ? 0 : 14,
              }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="col-start-1 row-start-1 font-semibold text-blue-400"
              style={{ pointerEvents: currentIndex === index ? "auto" : "none" }}
            >
              {word}
            </motion.span>
          ))}
        </span>
        <span className="mt-1 block text-white/40 md:hidden">{suffix}</span>

        <span className="relative hidden min-w-[200px] md:inline-block" aria-live="polite">
          {words.map((word, index) => (
            <motion.span
              key={`d-${word}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: currentIndex === index ? 1 : 0,
                y: currentIndex === index ? 0 : -20,
              }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className={`text-blue-400 ${currentIndex === index ? "relative" : "absolute left-0 top-0"}`}
            >
              {word}
            </motion.span>
          ))}
        </span>
        <span className="hidden text-white/40 md:inline"> {suffix}</span>
      </>
    );
  }

  return (
    <>
      <span className="block md:hidden">{t("hero.softwareFor")}</span>
      <span className="mt-1 inline-grid grid-cols-1 grid-rows-1 place-items-start md:hidden" aria-live="polite">
        <span className="invisible col-start-1 row-start-1 font-semibold" aria-hidden>
          {t("hero.invisibleSpacer")}
        </span>
        {words.map((word, index) => (
          <motion.span
            key={`m-${word}`}
            initial={false}
            animate={{
              opacity: currentIndex === index ? 1 : 0,
              y: currentIndex === index ? 0 : 14,
            }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="col-start-1 row-start-1 font-semibold text-blue-400"
            style={{ pointerEvents: currentIndex === index ? "auto" : "none" }}
          >
            {word}
          </motion.span>
        ))}
      </span>
      <span className="mt-1 block text-white/40 md:hidden">{t("hero.processingLine")}</span>
      <span className="block text-white/40 md:hidden">{t("hero.infrastructureLine")}</span>

      <span className="hidden md:inline">{t("hero.softwareFor")} </span>
      <span className="relative hidden min-w-[200px] md:inline-block" aria-live="polite">
        {words.map((word, index) => (
          <motion.span
            key={`d-${word}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{
              opacity: currentIndex === index ? 1 : 0,
              y: currentIndex === index ? 0 : -20,
            }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className={`text-blue-400 ${currentIndex === index ? "relative" : "absolute left-0 top-0"}`}
          >
            {word}
          </motion.span>
        ))}
      </span>
      <br className="hidden md:block" />
      <span className="hidden text-white/40 md:inline">{t("hero.processingInfrastructure")}</span>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-screen items-center overflow-hidden pb-16 pt-[max(5.5rem,calc(4.25rem+env(safe-area-inset-top,0px)))] sm:pb-20 sm:pt-24" data-testid="hero-section">
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div style={{ y, opacity }}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl"
          >
            <motion.h1
              variants={fadeUp}
              className="mb-6 text-4xl font-semibold leading-[1.15] tracking-tight text-white md:leading-[1.1] md:text-5xl lg:text-6xl"
            >
              <HeroHeadlineBlock />
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="text-lg md:text-xl text-white/50 leading-relaxed mb-10 max-w-2xl"
            >
              {t("hero.subtitle")}
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <button className="btn-primary group flex items-center gap-2 px-6 py-3 text-white font-medium rounded-lg" data-testid="hero-demo-btn">
                {t("hero.requestDemo")}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <a 
                href="https://carnitrack.samperlabs.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-secondary flex items-center gap-2 px-6 py-3 text-white/80 font-medium rounded-lg"
                data-testid="hero-carnitrack-btn"
              >
                {t("nav.carnitrack")}
                <ArrowRight size={16} />
              </a>
              <a 
                href="https://agritrack.samperlabs.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-secondary flex items-center gap-2 px-6 py-3 text-white/80 font-medium rounded-lg"
                data-testid="hero-agritrack-btn"
              >
                {t("nav.agritrack")}
                <ArrowRight size={16} />
              </a>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Dashboard Preview */}
        <motion.div
          initial={{ opacity: 0, y: 80, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-16"
        >
          <DashboardMockup />
        </motion.div>
      </div>
    </section>
  );
};

// Dashboard Mockup Component
const DashboardMockup = () => {
  const { t } = useTranslation();
  const sidebarItems = [
    t("dashboard.sidebarOverview"),
    t("dashboard.sidebarIntake"),
    t("dashboard.sidebarProcessing"),
    t("dashboard.sidebarYield"),
    t("dashboard.sidebarReports"),
  ];
  const stats = [
    { label: t("dashboard.statIntake"), value: "847", change: "+12%" },
    { label: t("dashboard.statProcessing"), value: "623", change: t("dashboard.statChangeActive") },
    { label: t("dashboard.statYield"), value: "78.4%", change: "+2.1%" },
    { label: t("dashboard.statRate"), value: "94/hr", change: t("dashboard.statChangeOnTarget") },
  ];
  const queueRows = [
    { id: "LOT-2847", stageKey: "queueWeighing", status: "active" },
    { id: "LOT-2848", stageKey: "queueProcessing", status: "active" },
    { id: "LOT-2849", stageKey: "queuePending", status: "pending" },
  ];

  return (
    <div className="dashboard-mockup dashboard-glow p-1" data-testid="dashboard-mockup">
      <div className="dashboard-header px-2 md:px-4 py-2 md:py-3 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-white/10" />
          <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-white/10" />
          <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-white/10" />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="px-2 md:px-4 py-1 rounded bg-white/5 text-[10px] md:text-xs text-white/40 mono truncate">
            {t("dashboard.urlHost")}
          </div>
        </div>
      </div>
      
      <div className="p-3 md:p-6 bg-[#0a0a0b]">
        <div className="grid grid-cols-12 gap-2 md:gap-4">
          <div className="col-span-2 hidden md:block">
            <div className="space-y-2">
              {sidebarItems.map((item, i) => (
                <div key={item} className={`px-3 py-2 rounded-lg text-xs ${i === 0 ? "bg-blue-500/20 text-blue-400" : "text-white/40"}`}>
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="col-span-12 md:col-span-10 space-y-3 md:space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
              {stats.map((stat) => (
                <div key={stat.label} className="metric-card p-2 md:p-4 rounded-lg border border-white/5">
                  <div className="text-[10px] md:text-xs text-white/40 mb-1">{stat.label}</div>
                  <div className="text-lg md:text-2xl font-semibold text-white">{stat.value}</div>
                  <div className="text-[10px] md:text-xs text-green-400 mt-1">{stat.change}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
              <div className="p-3 md:p-4 rounded-lg border border-white/5 bg-white/[0.02]">
                <div className="text-xs md:text-sm text-white/60 mb-3 md:mb-4">{t("dashboard.yieldTrend")}</div>
                <div className="flex items-end gap-1 md:gap-2 h-20 md:h-32">
                  {[65, 72, 68, 78, 82, 75, 84].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 bg-gradient-to-t from-blue-500/50 to-blue-400/30 rounded-t chart-bar"
                      style={{ height: `${h}%`, animationDelay: `${i * 0.1}s` }}
                    />
                  ))}
                </div>
              </div>

              <div className="p-3 md:p-4 rounded-lg border border-white/5 bg-white/[0.02] hidden md:block">
                <div className="text-sm text-white/60 mb-4">{t("dashboard.processingQueue")}</div>
                <div className="space-y-3">
                  {queueRows.map((item) => (
                    <div key={item.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`status-dot ${item.status === "active" ? "bg-green-400" : "bg-yellow-400"}`} />
                        <span className="text-sm text-white/80 mono">{item.id}</span>
                      </div>
                      <span className="text-xs text-white/40">{t(`dashboard.${item.stageKey}`)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Problem Section
const ProblemSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const problems = [
    {
      icon: <FileText size={24} />,
      key: "paper",
    },
    {
      icon: <Link2 size={24} />,
      key: "tracking",
    },
    {
      icon: <BarChart3 size={24} />,
      key: "visibility",
    },
    {
      icon: <GitBranch size={24} />,
      key: "supply",
    },
  ];

  return (
    <section className="py-24 md:py-32 relative z-10" data-testid="problem-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
        >
          <motion.div variants={slideInLeft} className="max-w-2xl mb-16">
            <span className="text-blue-400 text-sm font-medium uppercase tracking-wider mb-4 block">
              {t("problem.label")}
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
              {t("problem.title")}
            </h2>
            <p className="text-white/50 text-lg">
              {t("problem.body")}
            </p>
          </motion.div>

          <motion.div 
            variants={staggerFast}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {problems.map((problem, index) => (
              <motion.div
                key={problem.key}
                variants={scaleUp}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="feature-card p-6 rounded-xl border border-white/5 bg-white/[0.02] backdrop-blur-sm"
                data-testid={`problem-${index}`}
              >
                <div className="w-12 h-12 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 mb-4">
                  {problem.icon}
                </div>
                <h3 className="text-lg font-medium text-white mb-2">{t(`problem.items.${problem.key}.title`)}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{t(`problem.items.${problem.key}.description`)}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Platform Section
const PlatformSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    { icon: <Truck size={20} />, key: "tracking" },
    { icon: <Activity size={20} />, key: "realtime" },
    { icon: <Database size={20} />, key: "logs" },
    { icon: <TrendingUp size={20} />, key: "analytics" },
  ];

  return (
    <section id="platform" className="py-24 md:py-32 relative z-10" data-testid="platform-section">
      <div className="section-divider mb-24" />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div variants={slideInLeft}>
              <motion.span variants={fadeUp} className="text-blue-400 text-sm font-medium uppercase tracking-wider mb-4 block">
                {t("platform.label")}
              </motion.span>
              <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-semibold text-white mb-6">
                {t("platform.title")}
              </motion.h2>
              <motion.p variants={fadeUp} className="text-white/50 text-lg mb-10">
                {t("platform.body")}
              </motion.p>

              <motion.div variants={stagger} className="space-y-4">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.key}
                    variants={fadeUp}
                    custom={index}
                    whileHover={{ x: 10, transition: { duration: 0.2 } }}
                    className="flex items-start gap-4 p-4 rounded-lg hover:bg-white/[0.02] transition-colors cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 flex-shrink-0">
                      {feature.icon}
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1">{t(`platform.features.${feature.key}.title`)}</h4>
                      <p className="text-white/40 text-sm">{t(`platform.features.${feature.key}.desc`)}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div variants={slideInRight} className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-transparent rounded-2xl blur-3xl" />
              <motion.div 
                className="relative p-4 md:p-8 rounded-2xl border border-white/10 bg-[#111113]/80 backdrop-blur-sm"
                whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
              >
                <div className="grid grid-cols-2 gap-2 md:gap-4">
                  <MetricDisplay labelKey="platform.metricProcessed" value={12847} suffixKey="platform.suffixPerMo" />
                  <MetricDisplay labelKey="platform.metricYieldRate" value={78} suffixKey="platform.suffixPercent" />
                  <MetricDisplay labelKey="platform.metricProcessTime" value={24} suffixKey="platform.suffixMin" />
                  <MetricDisplay labelKey="platform.metricDataPoints" value={156} suffixKey="platform.suffixK" />
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Metric Display Component
const MetricDisplay = ({ labelKey, value, suffixKey }) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const count = useCounter(value, 2000, isInView);

  return (
    <div ref={ref} className="p-3 md:p-4 rounded-xl bg-white/[0.02] border border-white/5">
      <div className="text-[10px] md:text-xs text-white/40 mb-1 md:mb-2 truncate">{t(labelKey)}</div>
      <div className="text-xl md:text-3xl font-semibold text-white">
        {count.toLocaleString()}
        <span className="text-sm md:text-lg text-white/40">{t(suffixKey)}</span>
      </div>
    </div>
  );
};

// CarniTrack Section
const CarniTrackSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    { icon: <ClipboardCheck size={20} />, key: "intake" },
    { icon: <Scale size={20} />, key: "weight" },
    { icon: <Layers size={20} />, key: "stages" },
    { icon: <BarChart3 size={20} />, key: "yield" },
    { icon: <Shield size={20} />, key: "compliance" },
    { icon: <Database size={20} />, key: "history" },
  ];

  const gradeRows = [
    { gradeKey: "gradePrime", value: 32, color: "bg-green-500" },
    { gradeKey: "gradeChoice", value: 45, color: "bg-blue-500" },
    { gradeKey: "gradeSelect", value: 23, color: "bg-yellow-500" },
  ];

  return (
    <section id="carnitrack" className="py-24 md:py-32 relative z-10 bg-[#0d0d0f]/80 backdrop-blur-sm" data-testid="carnitrack-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
        >
          <div className="text-center max-w-2xl mx-auto mb-16">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-medium mb-6">
              <Cpu size={14} />
              {t("carnitrack.badge")}
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-semibold text-white mb-4">
              {t("carnitrack.title")}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-xl text-white/40 mb-6">
              {t("carnitrack.tagline")}
            </motion.p>
            <motion.a 
              variants={fadeUp}
              href="https://carnitrack.samperlabs.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm font-medium"
            >
              {t("carnitrack.visit")}
              <ArrowRight size={14} />
            </motion.a>
          </div>

          <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((feature, index) => (
              <div
                key={feature.key}
                className="card-shine feature-card p-6 rounded-xl border border-white/5 bg-white/[0.02]"
                data-testid={`carnitrack-feature-${index}`}
              >
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-white font-medium mb-2">{t(`carnitrack.features.${feature.key}.title`)}</h3>
                <p className="text-white/40 text-sm">{t(`carnitrack.features.${feature.key}.desc`)}</p>
              </div>
            ))}
          </motion.div>

          <motion.div variants={fadeUp} className="mt-16 p-8 rounded-2xl border border-white/5 bg-white/[0.01]">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <div className="text-sm text-white/40 mb-4">{t("carnitrack.yieldDistribution")}</div>
                <div className="space-y-3">
                  {gradeRows.map((item) => (
                    <div key={item.gradeKey}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-white/60">{t(`carnitrack.${item.gradeKey}`)}</span>
                        <span className="text-white">{item.value}%</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={isInView ? { width: `${item.value}%` } : {}}
                          transition={{ duration: 1, delay: 0.5 }}
                          className={`h-full ${item.color} rounded-full`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-sm text-white/40 mb-4">{t("carnitrack.weeklyThroughput")}</div>
                <div className="flex items-end gap-1 h-24">
                  {[85, 92, 78, 95, 88, 91, 87].map((v, i) => (
                    <motion.div
                      key={i}
                      initial={{ height: 0 }}
                      animate={isInView ? { height: `${v}%` } : {}}
                      transition={{ duration: 0.6, delay: i * 0.1 }}
                      className="flex-1 bg-gradient-to-t from-blue-600 to-blue-400 rounded-t"
                    />
                  ))}
                </div>
                <div className="flex justify-between mt-2 text-xs text-white/30">
                  <span>{t("carnitrack.dayMon")}</span>
                  <span>{t("carnitrack.daySun")}</span>
                </div>
              </div>

              <div>
                <div className="text-sm text-white/40 mb-4">{t("carnitrack.performanceScore")}</div>
                <div className="flex items-center justify-center h-24">
                  <div className="relative">
                    <svg className="w-24 h-24 transform -rotate-90">
                      <circle cx="48" cy="48" r="40" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
                      <motion.circle
                        cx="48"
                        cy="48"
                        r="40"
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth="8"
                        strokeLinecap="round"
                        strokeDasharray={251}
                        initial={{ strokeDashoffset: 251 }}
                        animate={isInView ? { strokeDashoffset: 251 * (1 - 0.94) } : {}}
                        transition={{ duration: 1.5, delay: 0.3 }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl font-semibold text-white">94%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// AgriTrack Expansion Section
const AgriTrackSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const bullets = ["b1", "b2", "b3", "b4"];

  return (
    <section className="py-24 md:py-32 expansion-gradient relative z-10" data-testid="agritrack-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
          className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
        >
          <motion.div variants={fadeUp}>
            <span className="inline-flex items-center gap-2 text-green-400 text-sm font-medium uppercase tracking-wider mb-4">
              <Zap size={14} />
              {t("agritrack.badge")}
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
              {t("agritrack.title")}
            </h2>
            <p className="text-white/50 text-lg mb-8">
              {t("agritrack.body")}
            </p>

            <div className="space-y-4 mb-8">
              {bullets.map((key) => (
                <div key={key} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Check size={12} className="text-green-400" />
                  </div>
                  <span className="text-white/70">{t(`agritrack.bullets.${key}`)}</span>
                </div>
              ))}
            </div>

            <a 
              href="https://agritrack.samperlabs.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors text-sm font-medium"
            >
              {t("agritrack.visit")}
              <ArrowRight size={14} />
            </a>
          </motion.div>

          <motion.div variants={fadeUp} className="relative">
            <div className="p-6 rounded-2xl border border-green-500/20 bg-green-500/5">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Target size={20} className="text-green-400" />
                </div>
                <div>
                  <div className="text-white font-medium">{t("agritrack.cardTitle")}</div>
                  <div className="text-xs text-white/40">{t("agritrack.cardSubtitle")}</div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03]">
                  <span className="text-sm text-white/60">{t("agritrack.flowLabel")}</span>
                  <span className="text-xs text-green-400 mono">{t("agritrack.statusConnected")}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03]">
                  <span className="text-sm text-white/60">{t("agritrack.dataContinuity")}</span>
                  <span className="text-xs text-green-400 mono">100%</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03]">
                  <span className="text-sm text-white/60">{t("agritrack.traceability")}</span>
                  <span className="text-xs text-green-400 mono">{t("agritrack.statusEndToEnd")}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Why SamperLabs Section
const WhySection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const reasons = [
    { icon: <Target size={24} />, key: "workflows" },
    { icon: <Shield size={24} />, key: "accuracy" },
    { icon: <Layers size={24} />, key: "scale" },
    { icon: <Cpu size={24} />, key: "integration" },
  ];

  return (
    <section className="py-24 md:py-32 relative z-10" data-testid="why-section">
      <div className="section-divider mb-24" />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
        >
          <motion.div variants={fadeUp} className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-blue-400 text-sm font-medium uppercase tracking-wider mb-4 block">
              {t("why.label")}
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              {t("why.title")}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {reasons.map((reason, index) => (
              <motion.div
                key={reason.key}
                variants={fadeUp}
                className="feature-card p-8 rounded-xl border border-white/5 bg-white/[0.02]"
                data-testid={`why-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 mb-5">
                  {reason.icon}
                </div>
                <h3 className="text-xl font-medium text-white mb-3">{t(`why.reasons.${reason.key}.title`)}</h3>
                <p className="text-white/50 leading-relaxed">{t(`why.reasons.${reason.key}.description`)}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Vision Section
const VisionSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const visionPoints = [
    { icon: <Link2 size={20} />, key: "connected" },
    { icon: <Activity size={20} />, key: "realtime" },
    { icon: <TrendingUp size={20} />, key: "predictive" },
    { icon: <Globe size={20} />, key: "transaction" },
  ];

  return (
    <section id="vision" className="py-24 md:py-32 relative z-10" data-testid="vision-section">
      <div className="absolute inset-0">
        <div className="gradient-orb w-[500px] h-[500px] bg-blue-600/10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
        >
          <motion.div variants={fadeUp} className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-blue-400 text-sm font-medium uppercase tracking-wider mb-4 block">
              {t("vision.label")}
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
              {t("vision.title")}
            </h2>
            <p className="text-white/50 text-lg">
              {t("vision.body")}
            </p>
          </motion.div>

          <motion.div variants={stagger} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {visionPoints.map((point, index) => (
              <motion.div
                key={point.key}
                variants={fadeUp}
                className="vision-card p-6 rounded-xl border border-white/10 bg-[#111113]/80 backdrop-blur text-center"
                data-testid={`vision-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 mx-auto mb-4">
                  {point.icon}
                </div>
                <h3 className="text-white font-medium mb-2">{t(`vision.points.${point.key}.title`)}</h3>
                <p className="text-white/40 text-sm">{t(`vision.points.${point.key}.desc`)}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// FAQ (homepage — visible content + FAQPage JSON-LD via SEOHead)
const FaqSection = () => {
  const { t } = useTranslation();
  const raw = t("faq.items", { returnObjects: true });
  const items = Array.isArray(raw) ? raw : [];
  if (items.length === 0) return null;
  return (
    <section id="faq" className="relative z-10 py-20 md:py-28" aria-labelledby="faq-heading">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <h2 id="faq-heading" className="mb-10 text-center text-3xl font-semibold text-white md:text-4xl">
          {t("faq.title")}
        </h2>
        <div className="space-y-3">
          {items.map((item, i) => (
            <details
              key={`${item.q}-${i}`}
              className="group rounded-xl border border-white/10 bg-[#111113]/80 px-5 py-1 backdrop-blur open:pb-4"
            >
              <summary className="cursor-pointer list-none py-4 font-medium text-white marker:content-none [&::-webkit-details-marker]:hidden">
                <span className="flex items-center justify-between gap-3">
                  {item.q}
                  <ChevronRight className="h-5 w-5 shrink-0 text-white/40 transition-transform group-open:rotate-90" />
                </span>
              </summary>
              <p className="border-t border-white/5 pt-3 text-sm leading-relaxed text-white/55">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  const { t } = useTranslation();
  return (
    <section id="contact" className="py-24 md:py-32 relative z-10" data-testid="cta-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative p-12 md:p-16 rounded-3xl border border-white/10 bg-gradient-to-br from-[#111113] to-[#0a0a0b] overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
          
          <div className="relative z-10 text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
              {t("cta.title")}
            </h2>
            <p className="text-white/50 text-lg mb-10">
              {t("cta.body")}
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <button className="btn-primary flex items-center gap-2 px-8 py-4 text-white font-medium rounded-xl" data-testid="cta-demo">
                {t("cta.requestDemo")}
                <ArrowRight size={18} />
              </button>
              <button className="btn-secondary px-8 py-4 text-white/80 font-medium rounded-xl" data-testid="cta-sales">
                {t("cta.contactSales")}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useTranslation();
  const contactHref = useHashHref("contact");
  const year = new Date().getFullYear();
  return (
    <footer className="py-12 border-t border-white/5 relative z-10" data-testid="footer">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
          <Link to="/" className="brand-logo-link shrink-0">
            <img
              src={SAMPERLABS_LOGO}
              alt={t("common.altLogo")}
              className="brand-logo-img"
            />
          </Link>

          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 md:justify-end" aria-label={t("footer.ariaNav")}>
            <a href="/#platform" className="text-sm text-white/40 hover:text-white transition-colors" data-testid="footer-about">{t("footer.about")}</a>
            <Link to="/slaughterhouse-software" className="text-sm text-white/40 hover:text-white transition-colors">{t("footer.slaughterhouseSoftware")}</Link>
            <Link to="/livestock-management" className="text-sm text-white/40 hover:text-white transition-colors">{t("footer.livestockManagement")}</Link>
            <Link to="/protein-supply-chain" className="text-sm text-white/40 hover:text-white transition-colors">{t("footer.proteinSupplyChain")}</Link>
            <a href="https://carnitrack.samperlabs.com" target="_blank" rel="noopener noreferrer" className="text-sm text-white/40 hover:text-white transition-colors" data-testid="footer-carnitrack">{t("nav.carnitrack")}</a>
            <a href="https://agritrack.samperlabs.com" target="_blank" rel="noopener noreferrer" className="text-sm text-white/40 hover:text-white transition-colors" data-testid="footer-agritrack">{t("nav.agritrack")}</a>
            <a href={contactHref} className="text-sm text-white/40 hover:text-white transition-colors" data-testid="footer-contact">{t("footer.contact")}</a>
          </nav>

          <div className="text-center text-sm text-white/30 md:text-right">
            {t("footer.copyright", { year })}
          </div>
        </div>
      </div>
    </footer>
  );
};

// Matrix rain: world-space Y + scroll offset so drops move with the grid (viewport-sized canvas)
const AnimatedBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId;
    let drops = [];

    const docHeight = () =>
      Math.max(
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );

    class Drop {
      constructor() {
        this.reset(true);
      }

      reset(initial = false) {
        const w = canvas.width;
        const h = docHeight();
        this.x = Math.random() * w;
        this.y = initial
          ? Math.random() * h
          : -this.length - Math.random() * 400;
        this.speed = 1 + Math.random() * 2;
        this.length = 40 + Math.random() * 120;
        this.opacity = 0.2 + Math.random() * 0.5;
      }

      update() {
        const h = docHeight();
        this.y += this.speed;
        if (this.y > h + this.length) {
          this.reset(false);
        }
      }

      draw(screenY) {
        const gradient = ctx.createLinearGradient(
          this.x,
          screenY,
          this.x,
          screenY + this.length
        );
        gradient.addColorStop(0, 'rgba(59, 130, 246, 0)');
        gradient.addColorStop(0.3, `rgba(59, 130, 246, ${this.opacity * 0.3})`);
        gradient.addColorStop(0.7, `rgba(59, 130, 246, ${this.opacity * 0.7})`);
        gradient.addColorStop(1, `rgba(59, 130, 246, ${this.opacity})`);

        ctx.beginPath();
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 1;
        ctx.moveTo(this.x, screenY);
        ctx.lineTo(this.x, screenY + this.length);
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(this.x, screenY + this.length, 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(59, 130, 246, ${this.opacity * 0.8})`;
        ctx.shadowColor = 'rgba(59, 130, 246, 0.8)';
        ctx.shadowBlur = 8;
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    }

    const syncDropCount = () => {
      const w = canvas.width;
      const h = docHeight();
      const target = Math.min(
        56,
        Math.max(24, Math.round((w * h) / 45000))
      );
      while (drops.length < target) {
        drops.push(new Drop());
      }
      while (drops.length > target) {
        drops.pop();
      }
    };

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      syncDropCount();
    };
    resize();
    window.addEventListener('resize', resize);

    const animate = () => {
      const scrollY = window.scrollY;
      const vh = window.innerHeight;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      drops.forEach((drop) => {
        drop.update();
        const screenY = drop.y - scrollY;
        if (screenY + drop.length > 0 && screenY < vh + 40) {
          drop.draw(screenY);
        }
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <div className="absolute inset-0 z-[1] pointer-events-none">
      <canvas
        ref={canvasRef}
        className="fixed inset-0 z-[1] block h-full w-full pointer-events-none"
        style={{ opacity: 0.8 }}
        aria-hidden
      />
      <div className="animated-gradient" />
      <div className="gradient-orb w-[500px] h-[500px] bg-blue-500/20 top-[-100px] right-[-100px]" />
      <div className="gradient-orb w-[400px] h-[400px] bg-blue-600/15 bottom-[30%] left-[-100px]" />
      <div className="gradient-orb w-[300px] h-[300px] bg-blue-500/10 top-[60%] right-[5%]" />
    </div>
  );
};

export function MarketingLayout() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const lang = i18n.language?.split("-")[0];
    document.documentElement.lang = lang === "tr" ? "tr" : "en";
  }, [i18n.language]);

  return (
    <div className="relative grid-pattern min-h-screen max-w-full overflow-x-clip">
      <AnimatedBackground />
      <Navigation />
      <Outlet />
      <Footer />
    </div>
  );
}

export function HomePage() {
  const { t } = useTranslation();
  const raw = t("faq.items", { returnObjects: true });
  const items = Array.isArray(raw) ? raw : [];
  const faqJsonLd =
    items.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: items.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }
      : null;

  return (
    <>
      <SEOHead
        title={t("seo.home.title")}
        description={t("seo.home.description")}
        canonicalPath="/"
        jsonLd={faqJsonLd}
      />
      <main className="relative z-10">
        <HeroSection />
        <ProblemSection />
        <PlatformSection />
        <CarniTrackSection />
        <AgriTrackSection />
        <WhySection />
        <VisionSection />
        <FaqSection />
        <CTASection />
      </main>
    </>
  );
}
